import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAuth } from "../contexts/AuthContext";

// Validation schema
const schema = yup.object({
  username: yup.string().required("Username is required"),
  password: yup
    .string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

const LoginPage = ({
  portalType,
  portalName,
  portalColor = "#1976d2",
  logo = null,
  redirectPath = "/dashboard",
  showPortalInfo = true,
}) => {
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loginLoading, setLoginLoading] = useState(false);

  // Debug: Log authentication state changes (removed to prevent re-render loops)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  // Redirect if already authenticated
  useEffect(() => {
    console.log(
      "🔄 useEffect triggered - isAuthenticated:",
      isAuthenticated,
      "loginLoading:",
      loginLoading
    );
    if (isAuthenticated && !loginLoading) {
      console.log("✅ useEffect: User is authenticated, redirecting...");
      const from =
        location.state?.from?.pathname || redirectPath || "/dashboard";
      console.log("🧭 useEffect: Navigating to:", from);

      // Check if we're on a login page (handles both dev and production paths)
      const currentPath = window.location.pathname;
      const isOnLoginPage =
        currentPath.endsWith("/login") || location.pathname === "/login";

      console.log(
        "🔍 Current path:",
        currentPath,
        "Location pathname:",
        location.pathname,
        "Is on login page:",
        isOnLoginPage
      );

      if (isOnLoginPage) {
        console.log("🚀 Attempting navigation with React Router to:", from);
        // Try React Router first
        navigate(from, { replace: true });

        // Fallback after a short delay
        setTimeout(() => {
          const newPath = window.location.pathname;
          const stillOnLogin = newPath.endsWith("/login");

          if (stillOnLogin) {
            console.log("🔄 React Router failed, using window.location");
            // For production, construct the full path
            const basePath = import.meta.env.MODE === "development" ? "" : "/";
            const fullPath = basePath + from;
            console.log("🌐 Redirecting to full path:", fullPath);
            window.location.replace(fullPath);
          } else {
            console.log("✅ Navigation successful to:", newPath);
            setLoginLoading(false);
          }
        }, 100);
      }
    }
  }, [
    isAuthenticated,
    loginLoading,
    navigate,
    location.state?.from?.pathname,
    redirectPath,
  ]);

  // Clear error when component mounts - FIXED: Removed clearError dependency
  useEffect(() => {
    clearError();
  }, []); // Empty dependency array - only run on mount

  const onSubmit = async (data) => {
    setLoginLoading(true);
    try {
      console.log("🔐 Submitting login form...");

      const result = await login({
        ...data,
        portalType,
      });

      if (result.success) {
        console.log(
          "✅ Login successful, navigation will be handled by useEffect"
        );
        // Set loading to false so useEffect can trigger navigation
        setLoginLoading(false);
      } else {
        console.error("❌ Login failed:", result.error);
        setLoginLoading(false);
      }
    } catch (error) {
      console.error("💥 Login error:", error);
      setLoginLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        minWidth: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #4365ff93 0%, #9670ff8e 100%)",
        py: 3, // Reduced from 4
        fontFamily:
          '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            {" "}
            {/* Reduced from 4 */}
            {logo && (
              <Box sx={{ mb: 2 }}>
                <img src={logo} alt="Logo" style={{ maxHeight: 80 }} />
              </Box>
            )}
            <Typography
              component="h1"
              variant="h3"
              align="center"
              sx={{
                color: "#ffffff",
                mb: 2,
                fontFamily:
                  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                fontWeight: 700,
              }}
            >
              HITPA Portal
            </Typography>
            {showPortalInfo && (
              <Card sx={{ mb: 3, bgcolor: `rgba(255, 255, 255, 0.98)` }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography
                    variant="h5"
                    align="center"
                    sx={{ color: portalColor, fontWeight: "bold" }}
                  >
                    {portalName}
                  </Typography>
                  <Typography
                    variant="body2"
                    align="center"
                    color="textSecondary"
                  >
                    Healthcare Insurance Portal
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>

          {/* Login Form */}
          <Paper
            elevation={0}
            sx={{
              p: 5,
              width: "100%",
              maxWidth: 420,
              borderRadius: 4,
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(20px)",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <Typography
              component="h2"
              variant="h5"
              align="center"
              gutterBottom
              sx={{
                fontFamily:
                  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                fontWeight: 600,
                color: "#1e293b",
                mb: 3,
              }}
            >
              Sign In
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              sx={{ mt: 1 }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                autoComplete="username"
                autoFocus
                {...register("username")}
                error={!!errors.username}
                helperText={errors.username?.message}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                {...register("password")}
                error={!!errors.password}
                helperText={errors.password?.message}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 2,
                  bgcolor: portalColor,
                  "&:hover": {
                    bgcolor: portalColor,
                    opacity: 0.9,
                  },
                }}
                disabled={loginLoading}
              >
                {loginLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Sign In"
                )}
              </Button>
            </Box>
          </Paper>

          {/* Footer */}
          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Typography variant="body2" color="textSecondary">
              © 2024 HITPA. All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;
